import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { getSuggestedResponse } from "../../services/api";
import { securelyParseJson } from "./utils";

const storageKeyBase = "airbox:ai-suggested-message:";
const conversationIdPrefix = "conversation-id:";
const threadIdPrefix = "thread-id:";

export const useLocalStorageSuggestedResponse = (
  thread,
  booking,
  property,
  guestName,
  additionalUserContext
) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { conversationId, threadId } = useParams();

  const prefixedId = conversationId
    ? `${conversationIdPrefix}${conversationId}`
    : `${threadIdPrefix}${threadId}`;
  const storageKey = `${storageKeyBase}${prefixedId}`;

  const parsedJson = useCallback(
    () => securelyParseJson(window.localStorage.getItem(storageKey)),
    [storageKey]
  );

  const storedSuggestedResponse = useCallback(
    () => parsedJson()?.suggestedResponse,
    [parsedJson]
  );

  const storedSuggestedResponseRecordId = useCallback(
    () => parsedJson()?.recordId,
    [parsedJson]
  );

  const fetchSuggestedResponse = useCallback(async () => {
    // get suggested response only if the last message is a guest message
    if (thread?.posts[0]?.is_guest) {
      setLoading(true);
      setError(null);

      try {
        const response = await getSuggestedResponse({
          bookingId: booking?.id,
          messages: thread.posts,
          propertyId: property.id,
          guestName,
          additionalUserContext
        });

        setLoading(false);
        window.localStorage.setItem(
          storageKey,
          JSON.stringify({
            // Keep track of the number of messages in the thread
            // So if the thread has new messages, we fetch a new suggestion
            messagesCount: thread.posts.length,
            suggestedResponse: response.data.ai_generated_response,
            recordId: response.data.ai_performance_record_id
          })
        );
      } catch (error) {
        setError(true);
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, [
    thread?.posts,
    booking?.id,
    property?.id,
    guestName,
    additionalUserContext,
    storageKey
  ]);

  const isReadyToFetchSuggestion = useMemo(
    () => property?.id && thread?.posts,
    [thread?.posts, property?.id]
  );

  useEffect(() => {
    if (!isReadyToFetchSuggestion) {
      return;
    }

    const storedResponse = parsedJson();
    if (storedResponse) {
      if (
        storedResponse.messagesCount > 0 &&
        storedResponse.messagesCount === thread?.posts?.length
      ) {
        setLoading(false);

        return;
      } else {
        // Suggested response is outdated
        window.localStorage.removeItem(storageKey);
      }
    }

    fetchSuggestedResponse();
  }, [
    property,
    thread,
    booking?.id,
    storageKey,
    storedSuggestedResponse,
    isReadyToFetchSuggestion,
    parsedJson,
    guestName,
    additionalUserContext,
    fetchSuggestedResponse
  ]);

  const removeSuggestedResponse = id => {
    window.localStorage.removeItem(
      `${storageKeyBase}${conversationIdPrefix}${id}`
    );
    window.localStorage.removeItem(`${storageKeyBase}${threadIdPrefix}${id}`);
  };

  return {
    loading,
    error,
    getSuggestedResponse: storedSuggestedResponse,
    getSuggestedResponseRecordId: storedSuggestedResponseRecordId,
    removeSuggestedResponse,
    fetchSuggestedResponse
  };
};
